import React, { useEffect } from 'react';
import { create } from 'zustand';
import { PriceDialogContent } from '../../Components/BuyBoxDialog/PriceDialogContent';

import { useMediaQuery } from '@mui/material';
import { ProductPrice } from '../../Components/BuyBoxDialog/ProductPrice';
import { BuyBoxType } from '../../Components/BuyBoxDialog/BuyBoxType';
import { PriceDialogContentProps, ResponsiveDialog } from '../../Components';
import { ProductRewardSearchResult } from '../../types/product';

interface BuyBoxDialogState {
  open: boolean;
  /** location.pathname captured when the dialog was opened, so navigation-away can be told apart from a click that opens on an already-changed path. */
  openedAtPath?: string;
  checkoutAllowedOfferType?: 'firstPartyOnly' | 'thirdPartyOnly' | 'both';
  close: () => void;
  listener?: (newState: Readonly<BuyBoxDialogState>) => void;
  onClick: PriceDialogContentProps['onBuyClick'];
  products: any[];
  onProductPageClick: PriceDialogContentProps['onOpenRetailerProductPageClick'];
  disabledCheckout?: boolean;
}

const useBuyBoxDialogStore = create<BuyBoxDialogState>((set) => ({
  open: false,
  checkoutAllowedOfferType: undefined,
  close: () => set({ open: false }),
  products: [],
  onClick: () => void null,
  onProductPageClick: () => void null,
  disabledCheckout: false,
}));

export const BuyBoxDialog = () => {
  const state = useBuyBoxDialogStore();

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const { open, close, openedAtPath, products, onClick, onProductPageClick, checkoutAllowedOfferType, disabledCheckout } = state;

  const onBuyClick = (product: ProductRewardSearchResult) => {
    onClick?.(product);
    close?.();
  };
  useEffect(() => {
    state.listener?.(state);
  }, [state]);

  // Close the dialog only when the user navigates AWAY from the path it was
  // opened on. Keying purely on `window.location.pathname` (read lazily on
  // re-render) ate the first click: the click that opens the dialog is also the
  // render where a *previous* pathname change (mount, or a pack-size/variant
  // switch made while the dialog was closed) is first noticed — so close() ran
  // and clobbered the just-set `open`. Comparing against the open-time path
  // makes that self-open a no-op while still closing on real navigation.
  React.useEffect(() => {
    if (open && openedAtPath && window.location.pathname !== openedAtPath) {
      close();
    }
  }, [open, openedAtPath, window.location.pathname]);

  return (
    <ResponsiveDialog
      open={open}
      disableScrollLock
      onClose={close}
      sx={(theme) => ({
        zIndex: 1400,
        '& div.MuiDialog-paper': {
          margin: '0 20px',
          width: '100%',
          maxWidth: 625,
          [theme.breakpoints.down('md')]: {
            maxWidth: '100%',
            margin: '0px 0px',
            padding: '20px 20px',
            boxSizing: 'border-box',
          },
        },
      })}
    >
      {!isMobile ? (
        <PriceDialogContent
          checkoutAllowedOfferType={checkoutAllowedOfferType}
          disabledCheckout={disabledCheckout}
          productsToList={products}
          onBuyClick={onBuyClick}
          onOpenRetailerProductPageClick={onProductPageClick}
        />
      ) : (
        <ProductPrice
          buyBoxType={BuyBoxType.BuyBoxBottomSheetAlwaysExpandedWithoutBackdrop}
          checkoutAllowedOfferType={checkoutAllowedOfferType}
          disabledCheckout={disabledCheckout}
          productsToList={products}
          onBuyClick={onBuyClick}
          onOpenRetailerProductPageClick={onProductPageClick}
        />
      )}
    </ResponsiveDialog>
  );
};

BuyBoxDialog.show = (props: Omit<BuyBoxDialogState, 'open' | 'close' | 'openedAtPath'>) => {
  useBuyBoxDialogStore.setState({
    ...props,
    open: true,
    openedAtPath: window.location.pathname,
  });
};

BuyBoxDialog.hide = () => {
  useBuyBoxDialogStore.setState({
    open: false,
  });
};
